/**
 * FERP Portal - Patient Context Selector
 *
 * DRR-318: Select a patient to filter all FHIR views to that patient.
 * Uses FocusContext (DRR-178) for persistence across tabs and pages.
 *
 * When a patient is selected:
 * - All FHIR entity tabs filter to that patient's records
 * - The selection persists during the session (Valkey-backed)
 * - Clear button returns to showing all patients
 */

import React, { useCallback, useMemo } from 'react';
import { Group, Select, Badge, ActionIcon, Tooltip } from '@mantine/core';
import { IconUser, IconX, IconFilter } from '@tabler/icons-react';
import { useFocusContext } from '@/contexts/FocusContext';
import { useAuth } from '@/auth/AuthContext';
import { useAdminListFhirPatientsQuery } from '@/generated/graphql';

interface PatientContextSelectorProps {
  /** Subgraph ID - only show for FHIR subgraph */
  subgraphId: string;
}

function extractPatientName(fhirJson: string): string | null {
  try {
    const patient = JSON.parse(fhirJson);
    if (patient.name?.[0]) {
      const name = patient.name[0];
      const given = Array.isArray(name.given) ? name.given.join(' ') : '';
      const family = name.family ?? '';
      return [given, family].filter(Boolean).join(' ') || null;
    }
    return null;
  } catch {
    return null;
  }
}

export function PatientContextSelector({ subgraphId }: PatientContextSelectorProps): React.ReactElement | null {
  const { focusedSubject, focusedDisplayName, isFocused, setFocus, clearFocus } = useFocusContext();
  const { tenant } = useAuth();

  // Only show for FHIR subgraph
  if (subgraphId !== 'fhir') {
    return null;
  }

  // Fetch patients for the dropdown
  const { data, loading } = useAdminListFhirPatientsQuery({
    variables: { tenantNum: tenant?.num ?? 0, limit: 100 },
    skip: !tenant?.num,
    fetchPolicy: 'cache-first',
  });

  // Build patient options for Select
  const patientOptions = useMemo(() => {
    if (!data?.adminListFhirPatients) return [];
    return data.adminListFhirPatients.map((patient) => {
      const name = extractPatientName(patient.fhirJson);
      return {
        value: patient.resourceId,
        label: name ?? patient.resourceId,
      };
    });
  }, [data]);

  const handleSelect = useCallback(
    (patientId: string | null) => {
      if (patientId) {
        const patient = data?.adminListFhirPatients?.find((p) => p.resourceId === patientId);
        const name = patient ? extractPatientName(patient.fhirJson) : null;
        setFocus(patientId, name ?? patientId, 'subject');
      } else {
        clearFocus();
      }
    },
    [data, setFocus, clearFocus],
  );

  return (
    <Group gap="xs">
      <IconFilter size={16} style={{ opacity: 0.6 }} />
      <Select
        placeholder="All Patients"
        value={focusedSubject}
        onChange={handleSelect}
        data={patientOptions}
        searchable
        clearable
        size="xs"
        w={200}
        leftSection={<IconUser size={14} />}
        disabled={loading}
        styles={{
          input: {
            fontWeight: isFocused ? 600 : 400,
          },
        }}
      />
      {isFocused && (
        <Tooltip label="Filtering by patient">
          <Badge
            color="blue"
            variant="light"
            size="sm"
            rightSection={
              <ActionIcon
                size="xs"
                variant="transparent"
                color="blue"
                onClick={() => clearFocus()}
              >
                <IconX size={10} />
              </ActionIcon>
            }
          >
            {focusedDisplayName ?? 'Patient'}
          </Badge>
        </Tooltip>
      )}
    </Group>
  );
}
